import * as React from "react";
import { Link } from "react-router-dom";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";
import VingleDialog, { VingleDialogTypes } from "app/vingleDialog";
import SolidReasonInput, {
  VALIDATION_ICON_TYPE,
} from "common/components/solidReasonInput";

import { VingleURL } from "@vingle/wtf-sdk/url";
const styles = require("app/account/edit/components/changePasswordDialog.scss");

interface IProps extends WrappedComponentProps {
  open: boolean;
  isLoading: boolean;
  doneHandler: (currentPassword: string, newPassword: string) => void;
  onRequestClose: () => void;
  initState?: () => void;
  failedToPasswordChange?: boolean;
}

interface IState {
  currentPassword: string;
  newPassword: string;
  newPasswordErrorMessage: string;
  rePassword: string;
  rePasswordErrorMessage: string;
  failedToPasswordChange?: boolean;
}

class ChangePasswordDialog extends React.PureComponent<IProps, IState> {
  public readonly state: IState = {
    currentPassword: "",
    newPassword: "",
    newPasswordErrorMessage: "",
    rePassword: "",
    rePasswordErrorMessage: "",
    failedToPasswordChange: this.props.failedToPasswordChange,
  };

  public componentDidUpdate(prevProps: IProps) {
    this.setState({
      failedToPasswordChange: this.props.failedToPasswordChange,
    });
    if (prevProps.open !== this.props.open) {
      this.initComponentState();
      if (this.props.initState) {
        this.props.initState();
      }
    }
  }

  public render() {
    const { intl, open, onRequestClose, isLoading } = this.props;
    const {
      currentPassword,
      newPassword,
      rePassword,
      rePasswordErrorMessage,
      failedToPasswordChange,
      newPasswordErrorMessage,
    } = this.state;

    let currentPasswordRightIcon: VALIDATION_ICON_TYPE;
    let newPasswordRightIcon: VALIDATION_ICON_TYPE;
    if (
      failedToPasswordChange === undefined ||
      newPasswordErrorMessage === ""
    ) {
      currentPasswordRightIcon = VALIDATION_ICON_TYPE.NONE;
      newPasswordRightIcon = VALIDATION_ICON_TYPE.NONE;
    } else if (failedToPasswordChange || newPasswordErrorMessage) {
      currentPasswordRightIcon = VALIDATION_ICON_TYPE.FAILED;
      newPasswordRightIcon = VALIDATION_ICON_TYPE.FAILED;
    } else {
      currentPasswordRightIcon = VALIDATION_ICON_TYPE.SUCCESS;
      newPasswordRightIcon = VALIDATION_ICON_TYPE.SUCCESS;
    }

    let rePasswordRightIcon: VALIDATION_ICON_TYPE;
    if (rePasswordErrorMessage === "") {
      rePasswordRightIcon = VALIDATION_ICON_TYPE.NONE;
    } else if (rePasswordErrorMessage) {
      rePasswordRightIcon = VALIDATION_ICON_TYPE.FAILED;
    } else {
      rePasswordRightIcon = VALIDATION_ICON_TYPE.SUCCESS;
    }

    const disableSubmit =
      rePasswordErrorMessage !== "" ||
      failedToPasswordChange ||
      this.isEmptyFromAllInput() ||
      isLoading;

    return (
      <VingleDialog
        open={open}
        type={VingleDialogTypes.BASIC}
        onRequestClose={onRequestClose}
        onRequestSubmit={this.handleSubmit}
        title={<FormattedMessage id="user_edit.password_change_title" />}
        disableSubmitButtons={disableSubmit}
      >
        <div className={styles.wrapper}>
          <div className={styles.field}>
            <label className={styles.subTitle}>
              <FormattedMessage id="user_edit.password_change_old_password" />
            </label>
            <SolidReasonInput
              wrapperClassName={styles.input}
              rightItemWrapperClassName={styles.inputRightItemWrapper}
              value={currentPassword}
              inputType="password"
              onChange={this.onChangeCurrentPassword}
              placeholder={intl.formatMessage({
                id: "user_edit.password_change_old_password",
              })}
              isDebounce={false}
              reason={
                failedToPasswordChange
                  ? intl.formatMessage({
                      id: "user_edit.password_change_failed_current_password",
                    })
                  : ""
              }
              rightIconType={currentPasswordRightIcon}
              autoFocus={true}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.subTitle}>
              <FormattedMessage id="user_edit.password_change_new_password" />
            </label>
            <SolidReasonInput
              wrapperClassName={styles.input}
              rightItemWrapperClassName={styles.inputRightItemWrapper}
              value={newPassword}
              inputType="password"
              onChange={this.onChangeNewPassword}
              placeholder={intl.formatMessage({
                id: "user_edit.password_change_new_password",
              })}
              isDebounce={false}
              rightIconType={newPasswordRightIcon}
              reason={
                failedToPasswordChange
                  ? intl.formatMessage({
                      id: "user_edit.password_change_failed_current_password",
                    })
                  : newPasswordErrorMessage
              }
            />
          </div>

          <div className={styles.field}>
            <label className={styles.subTitle}>
              <FormattedMessage id="user_edit.password_change_again_password" />
            </label>
            <SolidReasonInput
              wrapperClassName={styles.input}
              rightItemWrapperClassName={styles.inputRightItemWrapper}
              value={rePassword}
              inputType="password"
              onChange={this.onChangeRePassword}
              placeholder={intl.formatMessage({
                id: "user_edit.password_change_again_password",
              })}
              isDebounce={true}
              reason={rePasswordErrorMessage}
              rightIconType={rePasswordRightIcon}
            />
          </div>
          <Link
            className={styles.forgotIt}
            to={new VingleURL.ResetPassword().toString()}
          >
            <FormattedMessage id="user_edit.password_change_forgot_it" />
          </Link>
        </div>
      </VingleDialog>
    );
  }

  private readonly initComponentState = () => {
    this.setState({
      currentPassword: "",
      newPassword: "",
      newPasswordErrorMessage: "",
      rePassword: "",
      rePasswordErrorMessage: "",
      failedToPasswordChange: undefined,
    });
  };

  private readonly onChangeCurrentPassword = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value.trim();
    this.setState({
      currentPassword: value,
      failedToPasswordChange: undefined,
    });
  };

  private readonly onChangeNewPassword = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value.trim();
    if (value.length < 6) {
      this.setState({
        newPassword: value,
        newPasswordErrorMessage: this.props.intl.formatMessage({
          id: "signup.detail.password_error_short",
        }),
      });
    } else {
      this.setState({
        newPassword: value,
        newPasswordErrorMessage: "",
      });
    }
  };

  private readonly onChangeRePassword = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value.trim();
    this.checkValidateNewAndRePassword(value);
    this.setState({
      rePassword: value,
    });
  };

  private readonly checkValidateNewAndRePassword = (rePassword: string) => {
    if (this.state.newPassword !== rePassword) {
      this.setState({
        rePasswordErrorMessage: this.props.intl.formatMessage({
          id: "user_edit.password_change_not_match_re_new",
        }),
      });
    } else {
      this.setState({
        rePasswordErrorMessage: "",
      });
    }
  };

  private readonly isEmptyFromAllInput = () => {
    return (
      this.state.currentPassword === "" ||
      this.state.newPassword === "" ||
      this.state.rePassword === ""
    );
  };

  private readonly handleSubmit = () => {
    this.props.doneHandler(this.state.currentPassword, this.state.newPassword);
  };
}

export default injectIntl(ChangePasswordDialog);
